import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { api, externalApi } from '../../../../services/authService';
import { selectCurrentToken } from '../../../../redux/authSlice';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import MessageTemplateService from '../../../../services/messageTemplateService';
import moment from 'moment-timezone';
import BlastMessageConfig from '../../../../components/campaigns/blast/BlastMessageConfig';
import BlastScheduleConfig from '../../../../components/campaigns/blast/BlastScheduleConfig';
import BasicInfoForm from '../../../../components/campaigns/BasicInfoForm';
import TestBlastCampaign from '../../../../components/campaigns/blast/TestBlastCampaign';
import { validateStep } from '../../../../components/campaigns/CampaignValidation';
import CommunicationEndpointsProvider from '../../../../components/campaigns/CommunicationEndpointsProvider';
import OptOutConfig from '../../../../components/campaigns/OptOutConfig';
import RetryConfigurationPanel from '../../../../components/campaigns/RetryConfigurationPanel';
import { formatRetryConfigForBackend } from '../../../../utils/retryStrategyUtils';

// Get list of timezones from moment-timezone
const timezones = moment.tz.names();

const steps = [
  'Basic Information',
  'Message Configuration',
  'Schedule Settings',
  'Test',
  'Review & Activate'
];

const CreateBlastCampaign = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = useSelector(selectCurrentToken);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [crmCampaigns, setCrmCampaigns] = useState([]);
  const [statusChoices, setStatusChoices] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [communicationEndpoints, setCommunicationEndpoints] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    account: '',
    crm_campaign: '',
    start_date: '',
    end_date: '',
    active: true,
    is_ongoing: false,
    campaign_type: 'blast',
    channel: 'email',
    template: '',
    content: '',
    status: 'draft',
    auto_enroll_new_leads: false,
    auto_enroll_filters: [],
    // Retry configuration
    retry_strategy_id: null,
    max_retries: null,
    email_config: {
      content: '',
      subject: '',
      from_endpoint: '',
      from_name: '',
      reply_to: '',
      priority: 'normal',
      track_opens: true,
      track_clicks: true,
      attachments: []
    },
    sms_config: {
      content: '',
      from_endpoint: '',
      priority: 'normal',
      track_delivery: true,
      track_replies: true,
      media_urls: []
    },
    voice_config: {
      content: '',
      from_endpoint: '',
      voice: 'female',
      language: 'en-US',
      priority: 'normal',
      retry_attempts: 3,
      retry_delay: 300,
      record_call: true,
      call_timeout: 60,
      machine_detection: 'prefer_human'
    },
    chat_config: {
      content: '',
      from_endpoint: '',
      platform: 'whatsapp',
      priority: 'normal',
      track_delivery: true,
      track_read: true,
      media_urls: [],
      quick_replies: []
    },
    blast_schedule: {
      business_hours_only: true,
      send_time: '',
      timezone: 'UTC'
    },
    lead_filters: [],
    enable_opt_out: true,
    initial_opt_out_notice: "Reply STOP to opt out of further messages.",
    opt_out_message: "You’ve successfully opted out and will no longer receive messages from this number. Reply START at any time to resume.",
  });

  const [testData, setTestData] = useState({
    email: '',
    phone: '',
    testStatus: null,
    testError: null
  });

  // Load accounts, templates, and status choices on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [accountsResponse, templatesResponse, statusResponse] = await Promise.all([
          api.get('/crm/accounts/'),
          MessageTemplateService.getTemplates({ channel: formData.channel }),
          api.get('/acs/nurturing-campaigns/status_choices/')
        ]);
        setAccounts(accountsResponse.data);
        setTemplates(templatesResponse);
        setStatusChoices(statusResponse.data);
      } catch (err) {
        setError('Failed to load required data');
        console.error('Error loading data:', err);
      }
    };
    fetchData();
  }, [formData.channel]);

  // Fetch CRM campaigns when account changes
  useEffect(() => {
    const fetchCrmCampaigns = async () => {
      if (formData.account) {
        try {
          const response = await api.get(`/crm/accounts/${formData.account}/campaigns/`);
          setCrmCampaigns(response.data);
        } catch (err) {
          setError('Failed to load CRM campaigns');
          console.error('Error loading CRM campaigns:', err);
        }
      } else {
        setCrmCampaigns([]);
      }
    };
    fetchCrmCampaigns();
  }, [formData.account]);

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    
    if (name.startsWith('email_config.')) {
      const configKey = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        email_config: {
          ...prev.email_config,
          [configKey]: e.target.type === 'checkbox' ? checked : value,
        },
      }));
    } else if (name.startsWith('sms_config.')) {
      const configKey = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        sms_config: {
          ...prev.sms_config,
          [configKey]: e.target.type === 'checkbox' ? checked : value,
        },
      }));
    } else if (name.startsWith('voice_config.')) {
      const configKey = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        voice_config: {
          ...prev.voice_config,
          [configKey]: e.target.type === 'checkbox' ? checked : value,
        },
      }));
    } else if (name.startsWith('chat_config.')) {
      const configKey = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        chat_config: {
          ...prev.chat_config,
          [configKey]: e.target.type === 'checkbox' ? checked : value,
        },
      }));
    } else if (name.startsWith('blast_schedule.')) {
      const scheduleKey = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        blast_schedule: {
          ...prev.blast_schedule,
          [scheduleKey]: e.target.type === 'checkbox' ? checked : value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: e.target.type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async (activate = false) => {
    setLoading(true);
    setError(null);

    try {
      // Create a clean payload with only necessary fields
      const cleanPayload = {
        name: formData.name,
        description: formData.description,
        account: formData.account,
        crm_campaign: formData.crm_campaign,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        end_date: formData.is_ongoing ? null : (formData.end_date ? new Date(formData.end_date).toISOString() : null),
        active: formData.active,
        is_ongoing: formData.is_ongoing,
        campaign_type: 'blast',
        channel: formData.channel,
        template: formData.template || null,
        status: activate ? 'active' : 'draft',
        auto_enroll_new_leads: formData.auto_enroll_new_leads,
        auto_enroll_filters: formData.auto_enroll_filters,
        lead_filters: formData.lead_filters,
        enable_opt_out: formData.enable_opt_out ?? false,
        initial_opt_out_notice: formData.initial_opt_out_notice || '',
        opt_out_message: formData.opt_out_message || '',
        // Retry configuration - ensure proper integer formatting
        ...formatRetryConfigForBackend(formData.retry_strategy_id, formData.max_retries)
      };

      // Add the channel config
      const channelConfig = { ...formData[`${formData.channel}_config`] };
      
      // Set the from_endpoint_id to the from_endpoint value
      if (channelConfig.from_endpoint) {
        channelConfig.from_endpoint_id = channelConfig.from_endpoint;
      }

      // Add the channel config
      cleanPayload[`${formData.channel}_config`] = channelConfig;

      // Add the blast schedule config
      const scheduleConfig = { ...formData.blast_schedule };

      // Send the time exactly as selected in the UI, let backend handle timezone conversion
      if (scheduleConfig.send_time) {
        // Keep the time as is, just ensure it's in the correct format
        scheduleConfig.send_time = scheduleConfig.send_time;
      }

      cleanPayload.blast_schedule = scheduleConfig;

      // Create the campaign
      const campaignResponse = await api.post('/acs/blast-campaigns/', cleanPayload);

      // If activating, make the activation API call
      if (activate) {
        await api.post(`/acs/nurturing-campaigns/${campaignResponse.data.id}/activate/`);
      }

      // Navigate to lead selection page
      navigate(`/crm/lead-nurturing/${campaignResponse.data.id}/lead-selection`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create campaign');
    } finally {
      setLoading(false);
      setCreateDialogOpen(false);
    }
  };

  const handleTestCampaign = async () => {
    setTestData(prev => ({ ...prev, testStatus: 'loading', testError: null }));
    try {
      
      const message = formData.template ? 
        templates.find(t => t.id === formData.template)?.content : 
        formData[`${formData.channel}_config`].content;
      
      // Get the endpoint ID and value directly from the channel config
      const endpointId = formData[`${formData.channel}_config`].from_endpoint;
      const endpointValue = formData[`${formData.channel}_config`].from_endpoint_value;
      

      if (!message) {
        throw new Error('Message content is required');
      }

      if (!endpointValue) {
        throw new Error('From endpoint is required');
      }

      const testPayload = {
        phone_number: testData.phone,
        message: message,
        twilio_number: endpointValue // Use the stored endpoint value directly
      };
      
      const response = await externalApi.post('/communications/send-direct-sms/', testPayload);
      
      if (response.data.success) {
        setTestData(prev => ({ 
          ...prev, 
          testStatus: 'success', 
          testError: null 
        }));
      } else {
        throw new Error(response.data.detail || 'Failed to send test message');
      }
    } catch (err) {
      setTestData(prev => ({
        ...prev,
        testStatus: 'error',
        testError: err.response?.data?.detail || err.message || 'Failed to send test message'
      }));
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <BasicInfoForm
            formData={formData}
            onInputChange={handleInputChange}
            accounts={accounts}
            crmCampaigns={crmCampaigns}
            campaignTypes={{ blast: 'Blast Campaign' }}
          />
        );

      case 1:
        return (
          <CommunicationEndpointsProvider
            accountId={formData.account}
            campaignId={formData.crm_campaign}
            channel={formData.channel}
            activeTab={activeStep}
          >
            {({ endpoints, loading: endpointsLoading, error: endpointsError }) => (
              <Grid container spacing={3}>
                {endpointsError && (
                  <Grid item xs={12}>
                    <Alert severity="error">{endpointsError}</Alert>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <BlastMessageConfig
                    template={formData.template}
                    content={formData[`${formData.channel}_config`].content}
                    onTemplateChange={(value) => {
                      setFormData(prev => ({
                        ...prev,
                        template: value,
                        [`${prev.channel}_config`]: {
                          ...prev[`${prev.channel}_config`],
                          content: value ? '' : prev[`${prev.channel}_config`].content
                        }
                      }));
                    }}
                    onContentChange={(value) => {
                      setFormData(prev => ({
                        ...prev,
                        template: '',
                        [`${prev.channel}_config`]: {
                          ...prev[`${prev.channel}_config`],
                          content: value
                        }
                      }));
                    }}
                    templates={templates}
                    channel={formData.channel}
                    channelConfig={formData[`${formData.channel}_config`]}
                    onChannelConfigChange={(config) => {
                      setFormData(prev => ({
                        ...prev,
                        [`${prev.channel}_config`]: config
                      }));
                    }}
                    communicationEndpoints={endpoints}
                    onEndpointChange={(value) => {
                      setFormData(prev => ({
                        ...prev,
                        [`${prev.channel}_config`]: {
                          ...prev[`${prev.channel}_config`],
                          from_endpoint: value
                        }
                      }));
                    }}
                    onChannelChange={(value) => {
                      setFormData(prev => ({
                        ...prev,
                        channel: value,
                        template: '', // Reset template when channel changes
                        [`${value}_config`]: {
                          ...prev[`${value}_config`],
                          content: ''
                        }
                      }));
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <OptOutConfig
                    config={{
                      enable_opt_out: formData.enable_opt_out,
                      initial_opt_out_notice: formData.initial_opt_out_notice,
                      opt_out_message: formData.opt_out_message
                    }}
                    onChange={(updatedConfig) => {
                      setFormData(prev => ({
                        ...prev,
                        ...updatedConfig
                      }));
                    }}
                  />
                </Grid>
              </Grid>
            )}
          </CommunicationEndpointsProvider>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <BlastScheduleConfig
                schedule={formData.blast_schedule}
                onScheduleChange={(schedule) => {
                  setFormData(prev => ({
                    ...prev,
                    blast_schedule: {
                      ...prev.blast_schedule,
                      ...schedule
                    }
                  }));
                }}
                timezones={timezones}
              />
            </Grid>
            
            {/* Retry Configuration */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, mt: 2 }}>
                <RetryConfigurationPanel
                  retryStrategy={formData.retry_strategy_id}
                  maxRetries={formData.max_retries}
                  onRetryStrategyChange={(strategyId) => setFormData(prev => ({ ...prev, retry_strategy_id: strategyId }))}
                  onMaxRetriesChange={(maxRetries) => setFormData(prev => ({ ...prev, max_retries: maxRetries }))}
                  description="Configure retry behavior for blast messages"
                />
              </Paper>
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <CommunicationEndpointsProvider
            accountId={formData.account}
            campaignId={formData.crm_campaign}
            channel={formData.channel}
            activeTab={activeStep}
          >
            {({ endpoints, loading: endpointsLoading, error: endpointsError }) => (
              <TestBlastCampaign
                formData={formData}
                testData={testData}
                onTestDataChange={setTestData}
                onTestCampaign={handleTestCampaign}
                templates={templates}
                communicationEndpoints={endpoints}
              />
            )}
          </CommunicationEndpointsProvider>
        );

      case 4:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Review Campaign Details
              </Typography>
              <Paper sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1">Basic Information</Typography>
                    <Typography>Name: {formData.name}</Typography>
                    <Typography>Description: {formData.description}</Typography>
                    <Typography>
                      Account:{' '}
                      {accounts.find((a) => a.id === formData.account)?.name}
                    </Typography>
                    <Typography>Campaign Type: Blast Campaign</Typography>
                    <Typography>
                      Channel: {formData.channel.charAt(0).toUpperCase() + formData.channel.slice(1)}
                    </Typography>
                    <Typography>
                      Start Date: {formData.start_date || 'Not set'}
                    </Typography>
                    <Typography>
                      End Date: {formData.is_ongoing ? 'Ongoing' : (formData.end_date || 'Not set')}
                    </Typography>
                    <Typography>Active: {formData.active ? 'Yes' : 'No'}</Typography>
                    <Typography>Status: {formData.status}</Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle1">Message Configuration</Typography>
                    {formData.template ? (
                      <Typography>
                        Template:{' '}
                        {templates.find((t) => t.id === formData.template)?.name}
                      </Typography>
                    ) : (
                      <Typography>Content: {formData.content}</Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle1">Channel Configuration</Typography>
                    {formData.channel === 'email' && (
                      <>
                        <Typography>From Email: {formData.email_config.from_endpoint}</Typography>
                        <Typography>From Name: {formData.email_config.from_name}</Typography>
                        <Typography>Reply To: {formData.email_config.reply_to}</Typography>
                        <Typography>Priority: {formData.email_config.priority}</Typography>
                        {formData.email_config.attachments.length > 0 && (
                          <Typography>
                            Attachments: {formData.email_config.attachments.map(a => a.name).join(', ')}
                          </Typography>
                        )}
                      </>
                    )}

                    {formData.channel === 'sms' && (
                      <>
                        <Typography>From Number: {formData.sms_config.from_endpoint}</Typography>
                        <Typography>Priority: {formData.sms_config.priority}</Typography>
                        {formData.sms_config.media_urls.length > 0 && (
                          <Typography>
                            Media URLs: {formData.sms_config.media_urls.length} attached
                          </Typography>
                        )}
                      </>
                    )}

                    {formData.channel === 'voice' && (
                      <>
                        <Typography>From Number: {formData.voice_config.from_endpoint}</Typography>
                        <Typography>Voice: {formData.voice_config.voice}</Typography>
                        <Typography>Retry Attempts: {formData.voice_config.retry_attempts}</Typography>
                        <Typography>Machine Detection: {formData.voice_config.machine_detection}</Typography>
                        <Typography>Record Call: {formData.voice_config.record_call ? 'Yes' : 'No'}</Typography>
                        <Typography>Call Timeout: {formData.voice_config.call_timeout} seconds</Typography>
                      </>
                    )}

                    {formData.channel === 'chat' && (
                      <>
                        <Typography>Platform: {formData.chat_config.platform}</Typography>
                        <Typography>Priority: {formData.chat_config.priority}</Typography>
                        {formData.chat_config.quick_replies.length > 0 && (
                          <Typography>
                            Quick Replies: {formData.chat_config.quick_replies.map(r => r.text).join(', ')}
                          </Typography>
                        )}
                      </>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle1">Schedule Settings</Typography>
                    <Typography>
                      Timezone: {formData.blast_schedule.timezone}
                    </Typography>
                    <Typography>
                      Send Time: {formData.blast_schedule.send_time}
                    </Typography>
                    <Typography>
                      Business Hours Only:{' '}
                      {formData.blast_schedule.business_hours_only ? 'Yes' : 'No'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle1">Retry Configuration</Typography>
                    <Typography>
                      Retry Strategy: {formData.retry_strategy_id ? `Strategy ID: ${formData.retry_strategy_id}` : 'Default Strategy'}
                    </Typography>
                    <Typography>
                      Max Retries: {formData.max_retries || 'Use strategy default'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle1">Note</Typography>
                    <Typography>
                      After creating the campaign, you will be redirected to the lead selection page where you can configure which leads to include in this campaign.
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/crm/lead-nurturing')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" gutterBottom>
            Create Blast Campaign
          </Typography>
        </Box>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {renderStepContent(activeStep)}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          {activeStep > 0 && (
            <Button onClick={handleBack} sx={{ mr: 1 }}>
              Back
            </Button>
          )}
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={() => setCreateDialogOpen(true)}
              disabled={loading || !validateStep()}
            >
              {loading ? <CircularProgress size={24} /> : 'Create Campaign'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!validateStep()}
            >
              Next
            </Button>
          )}
        </Box>
      </Paper>

      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      >
        <DialogTitle>Create Campaign</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Choose how you want to create this campaign:
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => handleSubmit(false)} color="primary">
            Create as Draft
          </Button>
          <Button onClick={() => handleSubmit(true)} color="success" variant="contained">
            Create & Activate
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CreateBlastCampaign; 